'use client';

/**
 * Campervan Hire — Search & Results.
 *
 * Recreates the hifi handoff (design_handoff_campervan_hire_search) — hero
 * search card with van-type tabs + field row + popular routes, a sticky filter
 * rail, and a two-column grid of van result cards — reskinned onto the site's
 * warm sand/forest/terracotta tokens (the handoff's navy/blue is remapped).
 *
 * Results are driven by the real DriveAway /api/car-hire/search. The handoff's
 * card shows ratings, reviews and badges; our live inventory has none of that,
 * so those fields are intentionally omitted rather than fabricated. Every field
 * rendered — class, supplier, class photo, price/night (total ÷ nights), berth,
 * type — comes from the live offer. Filters that map to real data (van type,
 * berth parsed from the class string, price/night) are functional; the handoff's
 * feature checkboxes and map view have no data source and are not shown.
 */

import React, { useMemo, useState } from 'react';

const API_BASE =
  (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

// DriveAway's depot cities (a real pickup city is required for results).
const DEPOT_CITIES = [
  'Sydney', 'Melbourne', 'Brisbane', 'Cairns', 'Adelaide',
  'Perth', 'Darwin', 'Broome', 'Alice Springs', 'Hobart', 'Gold Coast',
];

// Van-type tabs → keyword matchers against the offer's car_class. 'budget'
// isn't a class, so it filters by an indicative per-night price ceiling.
type TabKey = 'all' | 'campervan' | 'motorhome' | '4x4' | 'budget';
// DriveAway only exposes instant pricing for CARS (campervan/motorhome pricing
// is quote-based), so this widget is the secondary "also need a car?" helper —
// only honest car-relevant tabs, no campervan/motorhome/4x4 (they'd never match
// a car class and read as misleading on a camping site).
const VAN_TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All cars' },
  { key: 'budget', label: 'Budget' },
];
const TAB_MATCHERS: Record<Exclude<TabKey, 'all' | 'budget'>, RegExp> = {
  campervan: /campervan|hi\s*-?\s*top|hitop|pop\s*-?\s*top|hi\s*ball|van/i,
  motorhome: /motor\s*home|motorhome|coach|winnebago/i,
  '4x4': /4\s*wd|4x4|four\s*wheel|cruiser|troop|hilux|off[\s-]?road|bushcamper|safari/i,
};
const BUDGET_PER_NIGHT_MAX = 120;

// Popular routes — clicking sets the pickup (and drop-off, one-way) cities.
const POPULAR_ROUTES: { label: string; from: string; to?: string }[] = [
  { label: 'Melbourne → Adelaide', from: 'Melbourne', to: 'Adelaide' },
  { label: 'Sydney → Byron Bay', from: 'Sydney', to: 'Gold Coast' },
  { label: 'Cairns → Cape Trib', from: 'Cairns' },
  { label: 'Perth → Esperance', from: 'Perth' },
  { label: 'The Big Lap', from: 'Sydney' },
  { label: 'Great Ocean Road', from: 'Melbourne' },
];

type SortKey = 'recommended' | 'price_low' | 'price_high';
const SORTS: { key: SortKey; label: string }[] = [
  { key: 'recommended', label: 'Recommended' },
  { key: 'price_low', label: 'Price: low to high' },
  { key: 'price_high', label: 'Price: high to low' },
];

type Offer = {
  supplier: string;
  supplier_code: string;
  car_class: string;
  class_code: string;
  price_aud: number;
  event_code: string;
  class_image: string | null;
  supplier_logo: string | null;
};

type SearchResult = {
  quote_id: string | null;
  offer_count: number;
  cheapest_aud: number | null;
  offers: Offer[];
  booking_url: string;
  cached?: boolean;
};

function isoPlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function nightsBetween(a: string, b: string): number {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(1, Math.round(ms / 86400000));
}

/** Berth parsed from a class string like "2 Berth Hitop" → "2" (or "6+"). */
function berthOf(carClass: string): string | null {
  const m = carClass.match(/(\d+)\s*berth/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return n >= 6 ? '6+' : String(n);
}

/** Short, honest feature tags derived from the class string + known inclusions. */
function tagsOf(carClass: string): string[] {
  const tags: string[] = [];
  const berth = berthOf(carClass);
  if (berth) tags.push(`${berth} berth`);
  if (/auto/i.test(carClass)) tags.push('Automatic');
  else if (/manual/i.test(carClass)) tags.push('Manual');
  if (TAB_MATCHERS['4x4'].test(carClass)) tags.push('4WD');
  if (/shower|toilet|ensuite/i.test(carClass)) tags.push('Shower + toilet');
  tags.push('Unlimited km');
  return tags.slice(0, 4);
}

export function CarHireSearch() {
  // ── search params ──────────────────────────────────────────────
  const [pickupCity, setPickupCity] = useState('Melbourne');
  const [oneWay, setOneWay] = useState(false);
  const [dropoffCity, setDropoffCity] = useState('');
  const [pickupDate, setPickupDate] = useState(isoPlusDays(14));
  const [dropoffDate, setDropoffDate] = useState(isoPlusDays(19));
  const [driverAge, setDriverAge] = useState(35);

  // ── result-view filters ────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [berth, setBerth] = useState<string | null>(null); // null = all
  const [maxPrice, setMaxPrice] = useState(400); // per-night ceiling
  const [sort, setSort] = useState<SortKey>('recommended');
  const [saved, setSaved] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [searchedCity, setSearchedCity] = useState('');
  const [searchedNights, setSearchedNights] = useState(0);

  async function runSearch() {
    setError(null);
    setResult(null);
    if (!pickupCity.trim()) {
      setError('Enter a pickup city.');
      return;
    }
    if (dropoffDate <= pickupDate) {
      setError('Drop-off date must be after pickup.');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        pickup_city: pickupCity.trim(),
        pickup_date: pickupDate,
        dropoff_date: dropoffDate,
        driver_age: String(driverAge),
      });
      if (oneWay && dropoffCity.trim()) params.set('dropoff_city', dropoffCity.trim());
      const res = await fetch(`${API_BASE}/car-hire/search?${params.toString()}`);
      if (!res.ok) {
        throw new Error(
          res.status === 503
            ? 'Live prices are temporarily unavailable — please try again in a moment.'
            : 'Search failed. Please try again.',
        );
      }
      const data: SearchResult = await res.json();
      setResult(data);
      setSearchedCity(pickupCity.trim());
      setSearchedNights(nightsBetween(pickupDate, dropoffDate));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  const nights = searchedNights || nightsBetween(pickupDate, dropoffDate);

  // Offers enriched with a per-night price + derived attributes, then filtered
  // by the active tab / berth / price, then sorted.
  const enriched = useMemo(() => {
    const offers = result?.offers ?? [];
    return offers.map((o) => ({
      offer: o,
      perNight: Math.round(o.price_aud / Math.max(1, nights)),
      berth: berthOf(o.car_class),
      tags: tagsOf(o.car_class),
    }));
  }, [result, nights]);

  const availableBerths = useMemo(() => {
    const set = new Set<string>();
    enriched.forEach((e) => { if (e.berth) set.add(e.berth); });
    return ['2', '3', '4', '6+'].filter((b) => set.has(b));
  }, [enriched]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (activeTab === 'budget') {
      list = list.filter((e) => e.perNight <= BUDGET_PER_NIGHT_MAX);
    } else if (activeTab !== 'all') {
      const re = TAB_MATCHERS[activeTab];
      list = list.filter((e) => re.test(e.offer.car_class));
    }
    if (berth) list = list.filter((e) => e.berth === berth);
    list = list.filter((e) => e.perNight <= maxPrice);
    const sorted = [...list];
    if (sort === 'price_low') sorted.sort((a, b) => a.perNight - b.perNight);
    else if (sort === 'price_high') sorted.sort((a, b) => b.perNight - a.perNight);
    return sorted;
  }, [enriched, activeTab, berth, maxPrice, sort]);

  function toggleSaved(code: string) {
    setSaved((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }

  return (
    <div>
      {/* ── Hero search card ─────────────────────────────────────── */}
      <div className="rounded-2xl bg-[var(--sand-2)] border border-[var(--line)] p-4 sm:p-5 cshadow">
        {/* Van-type tabs */}
        <div className="flex flex-wrap gap-2">
          {VAN_TABS.map((t) => {
            const on = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={[
                  'font-heading rounded-full px-4 py-2 text-[13.5px] font-bold transition',
                  on
                    ? 'bg-[var(--ink)] text-white'
                    : 'bg-[var(--sand)] text-[var(--ink-soft)] hover:bg-[rgba(229,225,219,0.6)]',
                ].join(' ')}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Field row */}
        <form
          onSubmit={(e) => { e.preventDefault(); void runSearch(); }}
          className="mt-3 grid gap-3 lg:grid-cols-[1.4fr_1fr_1fr_auto] lg:items-end"
        >
          <Field label="Pick-up location">
            <input
              list="ch-cities"
              value={pickupCity}
              onChange={(e) => setPickupCity(e.target.value)}
              placeholder="Melbourne, VIC"
              className="w-full bg-transparent text-[15.5px] font-semibold text-[var(--ink)] placeholder-[rgba(91,96,102,0.6)] focus:outline-none"
            />
            <datalist id="ch-cities">
              {DEPOT_CITIES.map((c) => <option key={c} value={c} />)}
            </datalist>
          </Field>
          <Field label="Pick-up">
            <input
              type="date"
              value={pickupDate}
              min={isoPlusDays(1)}
              onChange={(e) => setPickupDate(e.target.value)}
              className="w-full bg-transparent text-[15.5px] font-semibold text-[var(--ink)] focus:outline-none"
            />
          </Field>
          <Field label="Drop-off">
            <input
              type="date"
              value={dropoffDate}
              min={pickupDate}
              onChange={(e) => setDropoffDate(e.target.value)}
              className="w-full bg-transparent text-[15.5px] font-semibold text-[var(--ink)] focus:outline-none"
            />
          </Field>
          <button
            type="submit"
            disabled={loading}
            className="font-heading inline-flex min-h-[58px] items-center justify-center rounded-xl px-8 text-base font-extrabold text-white shadow-sm transition hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: 'var(--terra)' }}
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </form>

        {/* Secondary options — one-way + driver age (kept for a real search) */}
        <div className="mt-3 flex flex-wrap items-end gap-x-5 gap-y-2">
          <label className="flex items-center gap-2 text-[13px] font-medium text-[var(--ink-soft)]">
            <input type="checkbox" checked={oneWay} onChange={(e) => setOneWay(e.target.checked)} />
            Drop off in a different city
          </label>
          {oneWay && (
            <label className="text-[13px] text-[var(--ink-soft)]">
              <span className="mr-2 font-medium">Drop-off city</span>
              <input
                list="ch-cities"
                value={dropoffCity}
                onChange={(e) => setDropoffCity(e.target.value)}
                placeholder="e.g. Cairns"
                className="rounded-lg border border-[var(--line)] bg-white px-2.5 py-1.5 text-[13px] font-semibold text-[var(--ink)] focus:outline-none"
              />
            </label>
          )}
          <label className="text-[13px] text-[var(--ink-soft)]">
            <span className="mr-2 font-medium">Driver age</span>
            <input
              type="number"
              min={18}
              max={99}
              value={driverAge}
              onChange={(e) => setDriverAge(Number(e.target.value) || 35)}
              className="w-16 rounded-lg border border-[var(--line)] bg-white px-2.5 py-1.5 text-[13px] font-semibold text-[var(--ink)] focus:outline-none"
            />
          </label>
        </div>

        {/* Popular routes */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="eyebrow text-[var(--terra)] mr-1">Popular routes</span>
          {POPULAR_ROUTES.map((r) => (
            <button
              key={r.label}
              type="button"
              onClick={() => {
                setPickupCity(r.from);
                if (r.to) { setOneWay(true); setDropoffCity(r.to); }
              }}
              className="rounded-full border border-[var(--line)] bg-[var(--sand)] px-3 py-1.5 text-[13px] font-semibold text-[var(--ink-soft)] transition hover:bg-[rgba(229,225,219,0.5)]"
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Loading / error ──────────────────────────────────────── */}
      {loading && (
        <div className="mt-6 rounded-2xl border border-[var(--line)] bg-white/60 p-6 text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-[rgba(56,114,229,0.3)] border-t-[var(--forest)]" />
          <p className="text-sm font-semibold text-[var(--ink)]">Checking live prices across every major brand…</p>
          <p className="mt-1 text-xs text-[var(--ink-soft)]">This can take up to a minute — we&apos;re searching Sixt, Avis, Europcar, Budget, Hertz and more in real time.</p>
        </div>
      )}
      {error && !loading && (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {/* ── Results ──────────────────────────────────────────────── */}
      {result && !loading && (
        result.offers.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-6 text-sm text-[var(--ink-soft)]">
            No cars found for those dates and city. Try a different pickup city (DriveAway serves the
            major cities and airports) or adjust your dates.
          </div>
        ) : (
          <>
            {/* Results header */}
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-heading text-xl font-extrabold text-[var(--ink)]">
                {filtered.length} {filtered.length === 1 ? 'car' : 'cars'}
                <span className="ml-1 text-[15px] font-semibold text-[var(--ink-soft)]">
                  · {searchedCity} · {nights} {nights === 1 ? 'night' : 'nights'}
                </span>
              </h3>
              <label className="flex items-center gap-2 text-sm text-[var(--ink-soft)]">
                <span className="font-semibold">Sort</span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  className="rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm font-semibold text-[var(--ink)] focus:outline-none"
                >
                  {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </label>
            </div>

            {/* Body: filter rail + results grid */}
            <div className="mt-4 grid items-start gap-6 lg:grid-cols-[248px_1fr]">
              <FilterRail
                berths={availableBerths}
                berth={berth}
                onBerth={setBerth}
                maxPrice={maxPrice}
                onMaxPrice={setMaxPrice}
              />
              <div>
                {filtered.length === 0 ? (
                  <div className="rounded-2xl border border-[var(--line)] bg-white p-6 text-sm text-[var(--ink-soft)]">
                    No cars match these filters. Try a wider price or a different type.
                  </div>
                ) : (
                  <div className="grid gap-5 sm:grid-cols-2">
                    {filtered.map((e) => (
                      <VanCard
                        key={e.offer.event_code}
                        offer={e.offer}
                        perNight={e.perNight}
                        nights={nights}
                        tags={e.tags}
                        location={searchedCity}
                        bookingUrl={result.booking_url}
                        saved={saved.has(e.offer.event_code)}
                        onToggleSave={() => toggleSaved(e.offer.event_code)}
                      />
                    ))}
                  </div>
                )}
                <p className="mt-4 text-xs text-[rgba(91,96,102,0.8)]">
                  Per-night figure is the live total for your dates divided across {nights}{' '}
                  {nights === 1 ? 'night' : 'nights'}, incl. unlimited km. Book on DriveAway.
                </p>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

/** A labelled field cell in the hero search row. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[var(--line)] bg-[var(--sand)] px-3.5 py-2.5">
      <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[var(--ink-soft)]">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

/** Sticky filter rail — only the filters our live data can honestly power. */
function FilterRail({
  berths, berth, onBerth, maxPrice, onMaxPrice,
}: {
  berths: string[];
  berth: string | null;
  onBerth: (b: string | null) => void;
  maxPrice: number;
  onMaxPrice: (n: number) => void;
}) {
  const sectionLabel = 'text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--ink-soft)]';
  return (
    <aside className="lg:sticky lg:top-5 rounded-2xl border border-[var(--line)] bg-white p-[18px]">
      <h4 className="font-heading text-[15px] font-extrabold text-[var(--ink)]">Filters</h4>

      {berths.length > 0 && (
        <div className="mt-4">
          <div className={sectionLabel}>Berths</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Chip on={berth === null} onClick={() => onBerth(null)}>All</Chip>
            {berths.map((b) => (
              <Chip key={b} on={berth === b} onClick={() => onBerth(b)}>{b}</Chip>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5">
        <div className={sectionLabel}>Max price / night</div>
        <div className="mt-1 text-[13px] font-bold text-[var(--ink)]">
          $60 … ${maxPrice}
        </div>
        <input
          type="range"
          min={60}
          max={400}
          step={5}
          value={maxPrice}
          onChange={(e) => onMaxPrice(Number(e.target.value))}
          className="mt-2 w-full accent-[var(--terra)]"
        />
      </div>
    </aside>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-lg px-3 py-1.5 text-[13px] font-bold transition',
        on
          ? 'bg-[var(--ink)] text-white'
          : 'bg-[var(--sand)] text-[var(--ink-soft)] border border-[var(--line)] hover:bg-[rgba(229,225,219,0.5)]',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

/** Result card — hifi layout, only truthful fields from the live offer. */
function VanCard({
  offer, perNight, nights, tags, location, bookingUrl, saved, onToggleSave,
}: {
  offer: Offer;
  perNight: number;
  nights: number;
  tags: string[];
  location: string;
  bookingUrl: string;
  saved: boolean;
  onToggleSave: () => void;
}) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-white transition hover:shadow-[0_16px_34px_rgba(23,51,40,.14)]">
      {/* Image area */}
      <div className="relative h-[158px] overflow-hidden">
        {offer.class_image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={offer.class_image}
            alt={`${offer.car_class} hire car`}
            className="h-full w-full bg-[var(--sand)] object-contain p-3"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
          />
        ) : (
          <div
            className="h-full w-full"
            style={{
              background:
                'repeating-linear-gradient(45deg, var(--sand) 0 14px, var(--sand-2) 14px 28px)',
            }}
          />
        )}
        <button
          type="button"
          onClick={onToggleSave}
          aria-pressed={saved}
          aria-label={saved ? 'Remove from saved' : 'Save this car'}
          className="absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1.5 text-[12px] font-bold text-[var(--ink)] shadow-sm transition hover:bg-white"
        >
          <span className={saved ? 'text-[var(--terra)]' : ''}>{saved ? '♥' : '♡'}</span> Save
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <h4 className="font-heading text-[17px] font-extrabold leading-snug text-[var(--ink)]">
          {offer.car_class}
        </h4>
        <div className="mt-0.5 flex items-center gap-1.5 text-[13px] font-semibold text-[var(--ink-soft)]">
          {offer.supplier_logo && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={offer.supplier_logo}
              alt={offer.supplier}
              className="h-4 w-auto max-w-[54px] object-contain"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <span>{offer.supplier}{location ? ` · ${location}` : ''}</span>
        </div>

        {/* Feature tags */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {tags.map((t) => (
            <span
              key={t}
              className="rounded-md border border-[var(--line)] bg-[var(--sand)] px-2.5 py-1 text-[11.5px] font-semibold text-[var(--ink-soft)]"
            >
              {t}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-auto flex items-end justify-between gap-2 border-t border-[rgba(229,225,219,0.7)] pt-3">
          <div>
            <div className="font-heading text-2xl font-black leading-none text-[var(--ink)]">
              ${perNight}
              <span className="ml-1 text-[13px] font-semibold text-[var(--ink-soft)]">/night</span>
            </div>
            <div className="mt-0.5 text-[11.5px] text-[var(--ink-soft)]">
              AUD {offer.price_aud} total · {nights} {nights === 1 ? 'night' : 'nights'}
            </div>
          </div>
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="font-heading rounded-lg px-4 py-2.5 text-[13.5px] font-bold text-white transition"
            style={{ backgroundColor: 'var(--ink)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--terra)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--ink)'; }}
          >
            View deal
          </a>
        </div>
      </div>
    </article>
  );
}
